import { useEffect, useRef, useState } from "react";
import { Badge, Button, Carousel, ListGroup } from "react-bootstrap";
import { Graph } from "../types/Graph";
import { Reference } from "../types/Reference";
import { BsFillArrowDownCircleFill } from "react-icons/bs";
import { BLUE } from "../utils/Colors";

type Props = {
  graph: Graph;
  cycle: string[] | null;
  setCycle: (cycle: string[] | null) => void;
};

// A real component (not a plain function call) so each carousel owns its own
// hook scope. Rendering these via `{References(...)}` previously called
// useState a variable number of times per render, violating the Rules of
// Hooks and crashing the app when switching between the list and detail views.
function ReferencesCarousel({ references }: { references: Reference[] }) {
  const [index, setIndex] = useState(0);

  return (
    <Carousel
      activeIndex={index}
      onSelect={selectedIndex => setIndex(selectedIndex)}
      variant="dark"
      indicators={false}
      interval={null}
      controls={references.length > 1}
    >
      {references.map((reference, i) => (
        <Carousel.Item key={i} className="text-center">
          <div className="px-5">
            <div>
              <small className="text-muted">
                <code>
                  {reference.season}
                  {reference.episode}{" "}
                </code>
                {reference.start_time}
                <br></br>
              </small>
            </div>
            <small>{reference.text}</small>
          </div>
        </Carousel.Item>
      ))}
    </Carousel>
  );
}

function CycleFinder({ graph, cycle, setCycle }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);

  // When the selected cycle changes (opening a cycle's detail view, or going
  // back to the list), scroll the enclosing scrollable pane back to the top so
  // the user starts reading from the beginning instead of mid-list.
  useEffect(() => {
    let el: HTMLElement | null = rootRef.current;
    while (el) {
      const style = window.getComputedStyle(el);
      if (
        /(auto|scroll)/.test(style.overflowY) &&
        el.scrollHeight > el.clientHeight
      ) {
        el.scrollTop = 0;
        break;
      }
      el = el.parentElement;
    }
  }, [cycle]);

  if (!cycle) {
    return (
      <div className="p-3" ref={rootRef}>
        <h5>{graph.cycles.length} Cyclic TV Reference Paradoxes discovered</h5>
        <p>
          Click on a cycle below to view the exact references and visualize them
          on the graph.
        </p>
        <ListGroup>
          {graph.cycles.map((c, i) => (
            <ListGroup.Item
              key={i}
              className="d-flex justify-content-between align-items-start"
              action
              onClick={() => setCycle(c)}
            >
              <div>
                {c.map((title, j) => (
                  <small key={j}>
                    <code>{title}</code> {j === c.length - 1 ? "" : " -> "}
                  </small>
                ))}
              </div>
              <Badge bg="info" pill>
                {c.length}
              </Badge>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>
    );
  }

  return (
    <div className="p-3" ref={rootRef}>
      <Button variant="secondary" onClick={() => setCycle(null)}>
        Back
      </Button>
      {cycle.map((title, i) => {
        if (i < cycle.length - 1) {
          const references = graph.tvShows
            .get(title)!
            .referencesTo.get(cycle[i + 1])!;
          return (
            <div key={i} className="text-center">
              <div className="my-3">
                {title}
                <ReferencesCarousel references={references} />
              </div>
              <h5 style={{ color: BLUE }}>
                <BsFillArrowDownCircleFill />
              </h5>
            </div>
          );
        }
        return (
          <div key={i} className="text-center">
            {title}
          </div>
        );
      })}
    </div>
  );
}

export default CycleFinder;
